import { Component, OnInit, ViewChild } from '@angular/core';
import { HeaderColumn } from '../model/header-column.model';
import { DxCheckBoxComponent, DxDataGridComponent } from 'devextreme-angular';
import { ApiCommunicationService } from '../services/api-communication.service';
import { GridData } from '../model/grid-data.model';
import { HelperService } from '../services/helper.service';
import { HeaderType } from '../model/header-type.enum';
import { IncidentsService } from '../services/incidents.service';
import { ReportsService } from '../services/reports.service';

interface ColumnFilterRowSettings {
  filterValue: any;
  selectedFilterOperation: any;
}

interface ColumnHeaderFilterSettings {
  filterValues: any;
  filterType: any;
}

interface ColumnFilterSettings {
  dataField: string;
  filterRowSettings?: ColumnFilterRowSettings;
  headerFilterSettings?: ColumnHeaderFilterSettings;
}

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss']
})
export class OverviewComponent implements OnInit {

  constructor(private apiCommunicationService: ApiCommunicationService,
              private incidentsService: IncidentsService,
              private reportsService: ReportsService,
              private helperService: HelperService) {
  }

  @ViewChild('incidents')
  incidentsCheckBox: DxCheckBoxComponent;

  @ViewChild('reports')
  reportsCheckBox: DxCheckBoxComponent;

  @ViewChild('mixed')
  mixedCheckBox: DxCheckBoxComponent;

  @ViewChild('overviewDataGrid')
  overviewDataGrid: DxDataGridComponent;

  incidentManagementEnabled = false;
  incidentsCheckBoxId = 'incidentsCheckBox';
  reportsCheckBoxId = 'reportsCheckBox';
  mixedCheckBoxId = 'mixedCheckBox';
  data: any[] = [];
  headerColumns: HeaderColumn[] = [];
  isResetting = false;
  loadPanelConfiguration = {
    enabled: false,
    showPane: false
  };
  allowedPageSizes = [5, 10, 20, 50, 'all'];
  pagerDisplayMode = 'full';

  private currentHeaderType: HeaderType;
  private columnFilterSettings: ColumnFilterSettings[] = [];

  ngOnInit(): void {
    this.incidentManagementEnabled = this.helperService.getStartupConfiguration().incidentManagementEnabled;
    this.setupGrid(HeaderType.REPORTS);
    this.getRefreshGridNotification();
  }

  configurationValueChanged(data: any): void {
    if (this.isResetting) {
      this.isResetting = false;
      return;
    }
    if (!data?.element?.id || data?.component === undefined) {
      console.log('Unknown configuration checkbox clicked, specify component ID');
      return;
    }
    const id = data.element.id;
    if (this.isAllUnchecked()) {
      if (id === this.incidentsCheckBoxId) {
        this.isResetting = true;
        this.incidentsCheckBox.value = true;
      } else if (id === this.reportsCheckBoxId) {
        this.isResetting = true;
        this.reportsCheckBox.value = true;
      } else if (id === this.mixedCheckBoxId) {
        this.isResetting = true;
        this.mixedCheckBox.value = true;
      }
    } else {
      this.saveColumnFilters();
      if (id === this.incidentsCheckBoxId && this.incidentsCheckBox.value) {
        this.setupGrid(HeaderType.INCIDENTS);
        this.reportsCheckBox.value = false;
        this.mixedCheckBox.value = false;
      } else if (id === this.reportsCheckBoxId && this.reportsCheckBox.value) {
        this.setupGrid(HeaderType.REPORTS);
        this.incidentsCheckBox.value = false;
        this.mixedCheckBox.value = false;
      } else if (id === this.mixedCheckBoxId && this.mixedCheckBox.value) {
        this.setupGrid(HeaderType.MIXED);
        this.incidentsCheckBox.value = false;
        this.reportsCheckBox.value = false;
      }
    }
  }

  getGridWidth(): string {
    return this.incidentManagementEnabled ? 'calc(100vw - 350px)' : 'calc(100vw - 230px)';
  }

  edit(data: any): void {
    this.setFormEditMode(data);
  }

  rowDblClick(event: any): void {
    this.setFormEditMode(event.data);
  }

  resetGridFilters(): void {
    this.overviewDataGrid.instance.clearFilter();
  }

  collapseGridRows(): void {
    this.overviewDataGrid.instance.collapseAll(-1);
  }

  getContainerHeight(): string {
    return HelperService.getContentHeight();
  }

  getRowImage(data: any): string {
    const headerType: HeaderType = HeaderType[HeaderType[data.Type]];
    const image = headerType === HeaderType.REPORTS ? 'Report' : 'Incident';
    return `../../assets/images/main-menu/${image}.svg`;
  }

  private setFormEditMode(data): void {
    if (data.Type !== undefined && data.K3VRowKey !== undefined) {
      const headerType: HeaderType = HeaderType[HeaderType[data.Type]];
      if (headerType === HeaderType.REPORTS) {
        this.reportsService.editReportNotify(data.K3VRowKey);
      } else if (headerType === HeaderType.INCIDENTS) {
        this.incidentsService.editIncidentNotify(data.K3VRowKey);
      }
    }
  }

  private setupGrid(headerType: HeaderType): void {
    this.currentHeaderType = headerType;
    this.apiCommunicationService.getGridColumns(headerType).subscribe(columns => {
      columns.HeaderColumns.forEach(item => item.FieldName = item.FieldName.toUpperCase());
      this.headerColumns = columns.HeaderColumns.filter(column => column.IsVisible).sort(HelperService.sortGridHeaders);
      this.apiCommunicationService.getGridData(headerType).subscribe(gridData => {
        this.setGridData(gridData);
      });
    });
  }

  private resetGrid(): void {
    this.data = [];
    const dataGridInstance = this.overviewDataGrid.instance;
    dataGridInstance.getScrollable().scrollTo(0);
    dataGridInstance.collapseAll(-1);
    dataGridInstance.clearFilter();
    dataGridInstance.pageIndex(0);
  }

  private getRefreshGridNotification(): void {
    this.reportsService.getRefreshGridNotification().subscribe(refresh => {
      if (refresh) {
        this.apiCommunicationService.getGridData(this.currentHeaderType).subscribe(gridData => {
          this.setGridData(gridData);
        });
      }
    });
    this.incidentsService.getRefreshGridNotification().subscribe(refresh => {
      if (refresh) {
        this.apiCommunicationService.getGridData(this.currentHeaderType).subscribe(gridData => {
          this.setGridData(gridData);
        });
      }
    });
  }

  private setGridData(gridData: GridData): void {
    this.resetGrid();
    if (gridData === null) {
      return;
    }
    const data: any[] = [];
    gridData.Records.forEach(content => {
      const dataItem = {
        Type: content.Type,
        K3VRowKey: content.K3VRowKey
      };
      content.Fields.filter(field => field.IsVisible).forEach(field => {
        dataItem[field.FieldName.toUpperCase()] = field.Value;
      });
      data.push(dataItem);
    });
    this.data = data;
    this.applyColumnFilters();
  }

  private isAllUnchecked(): boolean {
    return this.incidentsCheckBox.value === false && this.reportsCheckBox.value === false && this.mixedCheckBox.value === false;
  }

  private saveColumnFilters(): void {
    this.columnFilterSettings = [];
    const visibleColumns = this.overviewDataGrid.instance.getVisibleColumns();
    visibleColumns.forEach(column => {
      const saveFilterRowSettings = column.filterValue !== undefined && column.filterValue !== null;
      const saveHeaderFilterSettings = column.filterValues !== undefined && column.filterValues !== null;
      if (saveFilterRowSettings || saveHeaderFilterSettings) {
        const settings: ColumnFilterSettings = {
          dataField: column.dataField
        };
        if (saveFilterRowSettings) {
          settings.filterRowSettings = {
            filterValue: column.filterValue,
            selectedFilterOperation: column.selectedFilterOperation
          };
        }
        if (saveHeaderFilterSettings) {
          settings.headerFilterSettings = {
            filterValues: column.filterValues,
            filterType: column.filterType
          };
        }
        const existing = this.columnFilterSettings.find(setting => setting.dataField === column.dataField);
        if (existing) {
          existing.filterRowSettings = settings.filterRowSettings;
          existing.headerFilterSettings = settings.headerFilterSettings;
        } else {
          this.columnFilterSettings.push(settings);
        }
      }
    });
  }

  private applyColumnFilters(): void {
    this.overviewDataGrid.instance.beginUpdate();
    this.columnFilterSettings.forEach(setting => {
      const visibleColumns = this.overviewDataGrid.instance.getVisibleColumns();
      const column = visibleColumns.find(item => item.dataField === setting.dataField);
      if (column) {
        if (setting.filterRowSettings) {
          this.overviewDataGrid.instance.columnOption(column.dataField, 'filterValue', setting.filterRowSettings.filterValue);
          this.overviewDataGrid.instance.columnOption(column.dataField, 'selectedFilterOperation', setting.filterRowSettings.selectedFilterOperation);
        }
        if (setting.headerFilterSettings) {
          this.overviewDataGrid.instance.columnOption(column.dataField, 'filterValues', setting.headerFilterSettings.filterValues);
          this.overviewDataGrid.instance.columnOption(column.dataField, 'filterType', setting.headerFilterSettings.filterType);
        }
      }
    });
    this.overviewDataGrid.instance.endUpdate();
  }
}
